import { useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CheckCircleSharpIcon from "@mui/icons-material/CheckCircleSharp";

import Text from "./text/Text";
import {
  btc,
  createLnAddress,
  pendingTransactions,
  sats,
  usdc,
  usdt,
} from "./messages";
import {
  copyIcon,
  createLnAddressIcon,
  createLnAddressIconUSDC,
  createLnAddressIconUSDT,
  hourGlassOrange,
} from "./images";
import usePendingTransactions from "../hooks/usePendingTransactions";
import WalletSymbols from "./WalletSymbols";
import TruncatedTextTooltip from "./TruncatedTextTooltip";
import { CustomTooltip } from "./tooltip/Tooltip";
import LnAddressQrModal from "./LnAddressQrModal";
import PendingTransactions from "./PendingTransactions";
import CommonSkeleton from "./CommonSkeleton";

const PendingTransactionCount = ({
  isFromBrowser,
  lnAddress,
  setIsOpenModal,
  lnLoader,
  isLnAddressSupported,
}) => {
  const navigate = useNavigate();
  const { taprootAsset } = useSelector((state) => state.common);
  const { uuid: currency } = { ...taprootAsset };

  const { transactions: pendingTransactionsList, isLoading: pendingTransactionLoader } =
    usePendingTransactions(currency);
  const totalUnconfirmedTransactions = pendingTransactionsList.length;

  const [isOpenPendingTransactionModal, setIsOpenPendingTransactionModal] =
    useState(false);
  const [copied, setCopied] = useState(false);
  const [isOpenLnAddressQrModal, setIsOpenLnAddressQrModal] = useState(false);

  const textSize = isFromBrowser ? 16 : 14;

  const handleNavigate = () => {
    isFromBrowser
      ? navigate("/pending-confirmations")
      : setIsOpenPendingTransactionModal(true);
  };

  const copyToClipboard = () => {
    navigator?.clipboard
      ?.writeText(lnAddress?.address)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1000);
      })
      .catch(() => {});
  };

  const renderLnAddressSection = () => {
    let icon;
    if (currency === sats) {
      icon = createLnAddressIcon;
    } else if (currency === usdt) {
      icon = createLnAddressIconUSDT;
    } else if (currency === usdc) {
      icon = createLnAddressIconUSDC;
    }

    const lnAddressSymbol = lnAddress ? (
      <Box>
        <CustomTooltip
          arrow={false}
          placement="top"
          text={
            <>
              <CheckCircleSharpIcon />
              <span>Copied</span>{" "}
            </>
          }
          open={copied}
        >
          <Box>
            <img
              src={copyIcon}
              height={24}
              width={24}
              alt="copy icon"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard();
              }}
            />
          </Box>
        </CustomTooltip>
      </Box>
    ) : (
      <WalletSymbols symbol="chevron_right" sx={{ color: "#848B9E" }} />
    );

    const lnAddressContent = lnAddress ? (
      <TruncatedTextTooltip
        textProps={{ font: "medium" }}
        textValue={lnAddress?.address}
        cellWidth={isFromBrowser ? "400px" : "320px"}
        fontSize={textSize}
      />
    ) : (
      <Box display="flex" alignItems="center">
        <Text size={textSize} font="regular">
          {createLnAddress(currency === sats ? btc : currency)}
        </Text>
      </Box>
    );

    return lnLoader ? (
      <CommonSkeleton width="100%" />
    ) : (
      <>
        <Box
          display="flex"
          alignItems="center"
          onClick={(e) => {
            lnAddress && copyToClipboard();
          }}
        >
          <img
            src={icon}
            alt="icon"
            style={{ marginRight: "4px" }}
            height="22px"
            width="22px"
          />
          {lnAddressContent}
        </Box>
        {lnAddressSymbol}
      </>
    );
  };

  const renderPendingTransactionSection = (type) => {
    return (
      <>
        <Box display="flex" alignItems="center">
          <img
            src={hourGlassOrange}
            alt="pending-transaction"
            style={{ marginRight: "5px" }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            height="22px"
            width="22px"
          />
          <Text
            className="default-class"
            font="medium"
            size={isFromBrowser ? 16 : 14}
            sx={{ marginRight: "5px" }}
          >
            {`${
              totalUnconfirmedTransactions > 10
                ? "10+"
                : totalUnconfirmedTransactions
            } ${pendingTransactions}${
              totalUnconfirmedTransactions > 1 ? "s" : ""
            }`}
          </Text>
        </Box>
        <WalletSymbols symbol="chevron_right" sx={{ color: "#848B9E" }} />
      </>
    );
  };

  const renderTransitionSection = ({ type }) => {
    return (
      <Box
        className={`border-box`}
        mt={
          isFromBrowser
            ? "6px"
            : type === "pendingTransaction"
              ? "6px 0"
              : "0px"
        }
        p={isFromBrowser ? "10px 8px" : "8px"}
        onClick={() =>
          type === "lnAddress"
            ? !lnAddress && setIsOpenModal(true)
            : handleNavigate()
        }
      >
        {type === "lnAddress"
          ? renderLnAddressSection(type)
          : renderPendingTransactionSection(type)}
      </Box>
    );
  };

  return (
    <>
      <Box
        display="flex"
        overflow="hidden"
        whiteSpace="nowrap"
        flexDirection="column"
      >
        {isLnAddressSupported &&
          renderTransitionSection({
            type: "lnAddress",
          })}
        {totalUnconfirmedTransactions > 0 &&
          !pendingTransactionLoader &&
          renderTransitionSection({
            type: "pendingTransaction",
          })}

        {isOpenPendingTransactionModal && (
          <PendingTransactions
            isOpenPendingTransactionModal={isOpenPendingTransactionModal}
            setIsOpenPendingTransactionModal={setIsOpenPendingTransactionModal}
          />
        )}
      </Box>
      {isOpenLnAddressQrModal && (
        <LnAddressQrModal
          isModalOpen={isOpenLnAddressQrModal}
          setIsModalOpen={(value) => setIsOpenLnAddressQrModal(value)}
          isFromBrowser={isFromBrowser}
          lnAddress={lnAddress}
        />
      )}
    </>
  );
};

export default PendingTransactionCount;
